/**
 * WorkflowContainerLine.tsx — Workflow 壳行 SSOT（D24 P1）
 *
 * 职责：● title (description) · meta · status 单行渲染。
 *
 * 链路位置：WorkflowChrome 根行 · WorkflowStageContainer 壳行。
 */
import type { CSSProperties } from 'react';

export interface WorkflowContainerLineProps {
  title: string;
  description?: string;
  uiStatus: string;
  meta?: string[];
  statusLabel: string;
  showDot?: boolean;
  variant?: 'root' | 'default';
  onToggle?: () => void;
  expanded?: boolean;
  /** false 时强制 div（done 折叠态避免与 ExpandTrigger 双 button 展开） */
  interactive?: boolean;
  titleTestId?: string;
  descriptionTestId?: string;
  headerTestId?: string;
  debugScopeKey?: string;
  verbose?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function WorkflowContainerLine({
  title,
  description,
  uiStatus,
  meta = [],
  statusLabel,
  showDot = true,
  variant = 'default',
  onToggle,
  expanded,
  interactive,
  titleTestId,
  descriptionTestId,
  headerTestId,
  debugScopeKey,
  verbose = false,
  className = '',
  style,
}: WorkflowContainerLineProps) {
  const lineClass = [
    'lax-workflow-container-line',
    variant === 'root' ? 'lax-workflow-container-line--root' : '',
    onToggle && interactive !== false ? 'lax-workflow-container-line--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {showDot ? (
        <span
          className="lax-workflow-container-line__status-dot lax-workflow-stage-container__status-dot"
          data-status={uiStatus}
          aria-hidden
        />
      ) : null}
      <span className="lax-workflow-container-line__title" data-testid={titleTestId}>
        {title}
      </span>
      {description ? (
        <span
          className="lax-workflow-container-line__desc"
          data-testid={descriptionTestId}
        >
          ({description})
        </span>
      ) : null}
      {meta.length > 0 ? (
        <span className="lax-workflow-container-line__meta" data-testid={titleTestId ? `${titleTestId}-meta` : undefined}>
          {meta.join(' · ')}
        </span>
      ) : null}
      {verbose && debugScopeKey ? (
        <span className="lax-workflow-container-line__debug" data-testid="lax-workflow-id">
          {debugScopeKey}
        </span>
      ) : null}
      <span className="lax-workflow-container-line__status">{statusLabel}</span>
    </>
  );

  if (onToggle && interactive !== false) {
    return (
      <button
        type="button"
        className={`${lineClass} lax-workflow-stage-container__header`}
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={headerTestId}
        style={style}
      >
        {content}
      </button>
    );
  }

  if (onToggle) {
    return (
      <div
        className={`${lineClass} lax-workflow-container-line--static-toggle`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        data-testid={headerTestId}
        style={style}
      >
        {content}
      </div>
    );
  }

  return (
    <div className={lineClass} style={style}>
      {content}
    </div>
  );
}
